import { create } from "zustand";
import type { HitlRequest } from "@/components/generative-ui";
import { httpRequest } from "@/lib/axios";

/** What the reviewer chose. All map to `POST .../resolve`. */
export type HitlDecisionOutcome = "approved" | "rejected" | "cancelled";

/**
 * A decision taken in the inbox: the request as it was when the reviewer
 * decided, plus who decided and when.
 *
 * The snapshot is what makes "Desfazer" possible — the card is off the pending
 * list during the undo window, so nothing else holds it (SCRUM-170).
 */
export interface HitlDecision {
  request: HitlRequest;
  outcome: HitlDecisionOutcome;
  /** Free text the reviewer wrote, sent as `decision_reason`. */
  answer?: string;
  /** Display name of the person who decided. */
  decidedBy: string;
  /** ISO instant the reviewer pressed the button, not the commit instant. */
  decidedAt: string;
}

interface HitlStoreState {
  pending: Map<string, HitlRequest>;
  /** Decided, inside the undo window, not sent to the API yet. */
  deferred: Map<string, HitlDecision>;
  /** Committed in this session, newest first. */
  resolved: HitlDecision[];
  connected: boolean;
  /**
   * This build carries no realtime client at all, and the pending list has
   * already been fetched. Not an error: the list on screen is accurate as of
   * the last load and only live updates are missing, so the UI says that
   * instead of claiming the approvals are unreachable.
   */
  realtimeDisabled: boolean;
  error: string | null;

  // Pusher bridge calls these
  upsert: (req: HitlRequest) => void;
  remove: (id: string) => void;
  setConnected: (c: boolean) => void;
  setRealtimeDisabled: (disabled: boolean) => void;
  setError: (msg: string | null) => void;

  // UI actions — optimistic; server confirms via Pusher
  approve: (id: string, answer?: string) => Promise<void>;
  reject: (id: string, answer?: string) => Promise<void>;
  cancel: (id: string) => Promise<void>;
  edit: (id: string, patch: Record<string, unknown>) => Promise<void>;

  // Deferred commit (SCRUM-170) — see `hitl-undo.ts` for the timer
  deferDecision: (decision: HitlDecision) => void;
  undoDecision: (id: string) => boolean;
  commitDecision: (id: string) => Promise<void>;
  /** Records a decision already sent through `approve`/`reject`/`edit`. */
  recordResolved: (decision: HitlDecision) => void;
}

function clone(m: Map<string, HitlRequest>) {
  return new Map(m);
}

function cloneDecisions(m: Map<string, HitlDecision>) {
  return new Map(m);
}

/** The body `POST /session/hitl/requests/{id}/resolve` expects for a decision. */
function resolveBody(decision: HitlDecision): Record<string, unknown> {
  return {
    state: decision.outcome,
    ...(decision.answer?.trim() ? { decision_reason: decision.answer.trim() } : {}),
  };
}

export const useHitlStore = create<HitlStoreState>((set, get) => ({
  pending: new Map(),
  deferred: new Map(),
  resolved: [],
  connected: false,
  realtimeDisabled: false,
  error: null,

  upsert: (req) =>
    set((s) => {
      const next = clone(s.pending);
      next.set(req.id, { ...next.get(req.id), ...req });
      return { pending: next };
    }),

  remove: (id) =>
    set((s) => {
      const next = clone(s.pending);
      next.delete(id);
      return { pending: next };
    }),

  setConnected: (c) => set({ connected: c }),
  setRealtimeDisabled: (disabled) => set({ realtimeDisabled: disabled }),
  setError: (msg) => set({ error: msg }),

  // Backend exposes a single POST /session/hitl/requests/{id}/resolve (the
  // tenant is the authenticated user's tenant, W0-05) that takes
  // { state: "approved" | "rejected" | "cancelled", decision_reason?, decision_edits?, expected_version? }
  // (see ResolveRequestRequest.php). approve/reject/edit are local UX
  // verbs that all map to the same endpoint with different state/edits.
  approve: async (id, answer) => {
    const prev = get().pending.get(id);
    if (!prev) throw new Error(`HITL ${id} not in store`);
    get().upsert({ ...prev, resolving: true });
    try {
      await httpRequest.post(`/session/hitl/requests/${id}/resolve`, {
        state: "approved",
        ...(answer?.trim() ? { decision_reason: answer.trim() } : {}),
      });
      // The server answered 2xx, so the decision is committed: drop the card
      // now instead of waiting for the realtime `.hitl.request.resolved` echo,
      // which never arrives when broadcasting is off (local stacks) and is a
      // reconciliation path, not the source of truth.
      get().remove(id);
    } catch (err) {
      get().upsert({ ...prev, resolving: false });
      throw err;
    }
  },

  reject: async (id, answer) => {
    const prev = get().pending.get(id);
    if (!prev) throw new Error(`HITL ${id} not in store`);
    get().upsert({ ...prev, resolving: true });
    try {
      await httpRequest.post(`/session/hitl/requests/${id}/resolve`, {
        state: "rejected",
        ...(answer?.trim() ? { decision_reason: answer.trim() } : {}),
      });
      // The server answered 2xx, so the decision is committed: drop the card
      // now instead of waiting for the realtime `.hitl.request.resolved` echo,
      // which never arrives when broadcasting is off (local stacks) and is a
      // reconciliation path, not the source of truth.
      get().remove(id);
    } catch (err) {
      get().upsert({ ...prev, resolving: false });
      throw err;
    }
  },

  edit: async (id, patch) => {
    const prev = get().pending.get(id);
    if (!prev) throw new Error(`HITL ${id} not in store`);
    get().upsert({ ...prev, resolving: true });
    try {
      // Edit = approve with overrides: caller sends the patched fields
      // and the action proceeds with those edits applied.
      await httpRequest.post(`/session/hitl/requests/${id}/resolve`, {
        state: "approved",
        decision_edits: patch,
      });
      // The server answered 2xx, so the decision is committed: drop the card
      // now instead of waiting for the realtime `.hitl.request.resolved` echo,
      // which never arrives when broadcasting is off (local stacks) and is a
      // reconciliation path, not the source of truth.
      get().remove(id);
    } catch (err) {
      get().upsert({ ...prev, resolving: false });
      throw err;
    }
  },

  cancel: async (id) => {
    const prev = get().pending.get(id);
    if (!prev) throw new Error(`HITL ${id} not in store`);
    get().upsert({ ...prev, resolving: true });
    try {
      await httpRequest.post(`/session/hitl/requests/${id}/resolve`, {
        state: "cancelled",
        decision_reason: "Aviso encerrado pela clínica, sem solicitar retomada da IA.",
      });
      get().remove(id);
    } catch (err) {
      get().upsert({ ...prev, resolving: false });
      throw err;
    }
  },

  /**
   * Takes the card off the pending list without calling the API. The decision
   * is only real when {@link commitDecision} runs, which is what makes the
   * 10 s "Desfazer" honest: nothing was sent while the toast was on screen.
   */
  deferDecision: (decision) =>
    set((s) => {
      const pending = clone(s.pending);
      pending.delete(decision.request.id);
      const deferred = cloneDecisions(s.deferred);
      deferred.set(decision.request.id, decision);
      return { pending, deferred };
    }),

  undoDecision: (id) => {
    const decision = get().deferred.get(id);
    if (!decision) return false;
    set((s) => {
      const pending = clone(s.pending);
      // The list is sorted by urgency then deadline (`sortPending`), so putting
      // the snapshot back is enough to restore the card's place.
      pending.set(id, { ...decision.request, resolving: false });
      const deferred = cloneDecisions(s.deferred);
      deferred.delete(id);
      return { pending, deferred };
    });
    return true;
  },

  commitDecision: async (id) => {
    const decision = get().deferred.get(id);
    if (!decision) return;
    try {
      await httpRequest.post(`/session/hitl/requests/${id}/resolve`, resolveBody(decision));
      set((s) => {
        const deferred = cloneDecisions(s.deferred);
        deferred.delete(id);
        return { deferred, resolved: [decision, ...s.resolved] };
      });
    } catch (err) {
      // The decision never reached the API, so the card goes back to the
      // pending list instead of disappearing (PRODUCT_GOVERNANCE rule 8).
      set((s) => {
        const pending = clone(s.pending);
        pending.set(id, { ...decision.request, resolving: false });
        const deferred = cloneDecisions(s.deferred);
        deferred.delete(id);
        return { pending, deferred };
      });
      throw err;
    }
  },

  recordResolved: (decision) => set((s) => ({ resolved: [decision, ...s.resolved] })),

}));

/**
 * Pure sort helper. Consumers should subscribe to `state.pending` directly and
 * derive the sorted array via `useMemo(() => sortPending(pendingMap), [pendingMap])`.
 *
 * Historically this was exposed as a `selectSortedPending` selector passed
 * straight to `useHitlStore(selector)`, but Zustand v5 + React 18
 * `useSyncExternalStore` requires a stable snapshot reference — a selector
 * that rebuilds the array on every call trips the "getSnapshot should be
 * cached" guard and escalates to a maximum-update-depth loop. Deriving in the
 * consumer with useMemo keeps the snapshot (the Map) stable.
 */
export function sortPending(pending: Map<string, HitlRequest>): HitlRequest[] {
  const urgencyRank = { high: 0, normal: 1, low: 2 };
  return [...pending.values()].sort((a, b) => {
    const u = urgencyRank[a.urgency] - urgencyRank[b.urgency];
    if (u !== 0) return u;
    return a.deadline.localeCompare(b.deadline);
  });
}

/** The decisions of the current day, newest first. */
export function resolvedToday(
  resolved: readonly HitlDecision[],
  now: Date = new Date()
): HitlDecision[] {
  const sameDay = (iso: string) => {
    const at = new Date(iso);
    return (
      at.getFullYear() === now.getFullYear() &&
      at.getMonth() === now.getMonth() &&
      at.getDate() === now.getDate()
    );
  };
  return resolved.filter((decision) => sameDay(decision.decidedAt));
}
